import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import {
  adminGetPlans, adminCreatePlan, adminUpdatePlan, adminDeletePlan,
  adminGetPackages, adminCreatePackage, adminUpdatePackage, adminDeletePackage
} from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, CreditCard, Package, Clock, Loader2, MessageSquare } from 'lucide-react';

export function AdminPlansPage() {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    minutes_per_month: '',
    message_count: '',
    facebook_message_count: '',
    features: ''
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await adminGetPlans();
      setPlans(response.data.plans);
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (plan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name,
      description: plan.description,
      price: plan.price.toString(),
      minutes_per_month: plan.minutes_per_month.toString(),
      message_count: plan.message_count?.toString() || '',
      facebook_message_count: plan.facebook_message_count?.toString() ?? '',
      features: plan.features.join('\n')
    });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingPlan(null);
    setForm({ name: '', description: '', price: '', minutes_per_month: '', message_count: '', facebook_message_count: '', features: '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        name: form.name,
        description: form.description,
        price: parseFloat(form.price),
        minutes_per_month: parseInt(form.minutes_per_month),
        ...(form.message_count.trim() !== '' ? { message_count: parseInt(form.message_count) } : {}),
        ...(form.facebook_message_count.trim() !== ''
          ? { facebook_message_count: parseInt(form.facebook_message_count, 10) }
          : {}),
        features: form.features.split('\n').filter(f => f.trim())
      };

      if (editingPlan) {
        await adminUpdatePlan(editingPlan.plan_id, data);
        toast.success('Plan updated');
      } else {
        await adminCreatePlan(data);
        toast.success('Plan created');
      }
      setDialogOpen(false);
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!confirm('Are you sure you want to deactivate this plan?')) return;
    try {
      await adminDeletePlan(planId);
      toast.success('Plan deactivated');
      loadPlans();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  if (loading) {
    return <ManagementSkeleton title="Subscription Plans" />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-plans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-jakarta font-bold text-slate-900">Subscription Plans</h1>
          <p className="text-slate-500">Manage your pricing plans</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNewDialog} data-testid="add-plan-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Plan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Professional"
                  data-testid="plan-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="For growing businesses"
                />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Price (USD)</Label>
                  <Input
                    type="number"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="79"
                    data-testid="plan-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Minutes/Month</Label>
                  <Input
                    type="number"
                    value={form.minutes_per_month}
                    onChange={(e) => setForm({ ...form, minutes_per_month: e.target.value })}
                    placeholder="500"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Message count</Label>
                  <Input
                    type="number"
                    value={form.message_count}
                    onChange={(e) => setForm({ ...form, message_count: e.target.value })}
                    placeholder="1000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Facebook / month</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.facebook_message_count}
                    onChange={(e) => setForm({ ...form, facebook_message_count: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Features (one per line)</Label>
                <Textarea
                  value={form.features}
                  onChange={(e) => setForm({ ...form, features: e.target.value })}
                  placeholder="500 minutes/month&#10;Call transcripts"
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-plan-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingPlan ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Message quota</TableHead>
                <TableHead>Facebook / mo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.plan_id} data-testid={`plan-row-${plan.plan_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{plan.name}</p>
                        <p className="text-sm text-slate-500 truncate max-w-[200px]">{plan.description}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">{formatCurrency(plan.price)}/mo</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-slate-600">
                      <Clock className="w-4 h-4" />
                      {plan.message_count ?? plan.minutes_per_month}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-slate-600">
                      <MessageSquare className="w-4 h-4 text-blue-500" />
                      {plan.facebook_message_count ?? 0}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={plan.is_active ? 'default' : 'secondary'}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(plan)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(plan.plan_id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export function AdminPackagesPage() {
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    minutes: '',
    message_count: '',
    price: ''
  });

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      const response = await adminGetPackages();
      setPackages(response.data.packages);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (pkg) => {
    setEditingPackage(pkg);
    setForm({
      name: pkg.name,
      minutes: pkg.minutes != null ? String(pkg.minutes) : '',
      message_count:
        pkg.message_count != null && pkg.message_count !== ''
          ? String(pkg.message_count)
          : '',
      price: pkg.price != null ? String(pkg.price) : ''
    });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingPackage(null);
    setForm({ name: '', minutes: '', message_count: '', price: '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const minutes = parseInt(form.minutes, 10);
    const messageCount =
      form.message_count === '' || form.message_count === undefined
        ? 0
        : parseInt(form.message_count, 10);
    const price = parseFloat(form.price);

    if (form.name.trim() === '') {
      toast.error('Name is required');
      return;
    }
    if (Number.isNaN(minutes) || minutes < 0) {
      toast.error('Minutes must be a non-negative number');
      return;
    }
    if (Number.isNaN(messageCount) || messageCount < 0) {
      toast.error('Message count must be a non-negative number');
      return;
    }
    if (Number.isNaN(price) || price < 0) {
      toast.error('Price must be a valid non-negative amount');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: form.name.trim(),
        minutes,
        message_count: messageCount,
        price
      };

      if (editingPackage) {
        await adminUpdatePackage(editingPackage.package_id, data);
        toast.success('Package updated');
      } else {
        await adminCreatePackage(data);
        toast.success('Package created');
      }
      setDialogOpen(false);
      loadPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (packageId) => {
    if (!confirm('Are you sure you want to deactivate this package?')) return;
    try {
      await adminDeletePackage(packageId);
      toast.success('Package deactivated');
      loadPackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  if (loading) {
    return <ManagementSkeleton title="Minute Packages" />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-packages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-jakarta font-bold text-slate-900">Minute Packages</h1>
          <p className="text-slate-500">Manage add-on minute packages</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNewDialog} data-testid="add-package-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Package
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingPackage ? 'Edit Package' : 'Create Package'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="500 Minutes Pack"
                  data-testid="package-name-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Minutes</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.minutes}
                    onChange={(e) => setForm({ ...form, minutes: e.target.value })}
                    placeholder="500"
                    data-testid="package-minutes-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Message count</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.message_count}
                    onChange={(e) => setForm({ ...form, message_count: e.target.value })}
                    placeholder="0"
                    data-testid="package-message-count-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price (USD)</Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="60"
                    data-testid="package-price-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-package-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingPackage ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Package</TableHead>
                <TableHead>Minutes</TableHead>
                <TableHead>Messages</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Per Minute</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.package_id} data-testid={`package-row-${pkg.package_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                        <Package className="w-5 h-5 text-purple-600" />
                      </div>
                      <p className="font-medium text-slate-900">{pkg.name}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-slate-600">
                      <Clock className="w-4 h-4" />
                      {pkg.minutes ?? '—'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-slate-600">
                      <MessageSquare className="w-4 h-4" />
                      {pkg.message_count != null ? pkg.message_count : '—'}
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">{formatCurrency(pkg.price)}</TableCell>
                  <TableCell className="text-slate-500">{formatCurrency(pkg.price_per_minute)}</TableCell>
                  <TableCell>
                    <Badge variant={pkg.is_active ? 'default' : 'secondary'}>
                      {pkg.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(pkg)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(pkg.package_id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ManagementSkeleton({ title }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>
      <Card className="border-0 shadow-md">
        <CardContent className="p-6 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="w-10 h-10 rounded-lg" />
              <div className="flex-1">
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-3 w-48" />
              </div>
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
